import clsx from 'clsx';
import { forwardRef, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { getStratoLogger } from '../../core/sdk/sdk-logger.js';
import * as styles from './CodeHighlight.sty.js';
import {
  HighlightExtended,
  type HighlightLanguage,
} from './HighlightExtended.js';
import { splitCodeForHighlighting } from './utils/highlighting-utils.js';

/**
 * Renders the non-highlighted tail of the code as a single table-row.
 *
 * When the block contains multiple lines, `whiteSpace: 'pre'` is enforced so
 * the content never wraps — all non-highlighted lines live in a single
 * table-row and wrapping would cause the line-number cell to go out of sync.
 * When the remainder is a single line (e.g. one huge log without newlines) it
 * inherits the parent's `whiteSpace` so the `lineBreaks` prop can take effect.
 */
function NonHighlightedBlock({
  showLineNumbers,
  size,
  isMultiLine,
  lineNumbers,
  code,
}: {
  showLineNumbers: boolean;
  size: 'default' | 'condensed';
  isMultiLine: boolean;
  lineNumbers: string;
  code: string;
}) {
  return (
    <div className={styles.line}>
      {showLineNumbers ? (
        <span
          className={clsx(
            'line-number',
            styles.lineNumberContainer({ multiLine: isMultiLine }),
          )}
        >
          <span
            className={styles.lineNumber({
              size,
              multiLine: isMultiLine,
            })}
          >
            {lineNumbers}
          </span>
        </span>
      ) : null}
      <span
        className={styles.lineContent({
          size,
          multiLine: isMultiLine,
        })}
      >
        {code}
      </span>
    </div>
  );
}

interface CodeHighlightProps {
  /**
   * Whether line numbers should be shown.
   * @defaultValue true
   */
  showLineNumbers?: boolean;
  /**
   * The code language of the snippet for syntax highlighting.
   */
  language?: HighlightLanguage;
  /**
   * The code snippet to be highlighted.
   */
  code: string;
  /**
   * Whether the content breaks into new lines automatically.
   * @defaultValue false
   */
  lineBreaks?: boolean;
  /**
   * Max height of the code container in px.
   */
  maxHeight?: number;
  /**
   * Layout size, which affects font size, padding and margins.
   * @defaultValue 'default'
   */
  size?: 'default' | 'condensed';
}

/**
 * Highlights code with prism tokenization.
 * @internal
 */
export const CodeHighlight = /* @__PURE__ */ forwardRef<
  HTMLPreElement,
  CodeHighlightProps
>((props, forwardedRef) => {
  const {
    code,
    language,
    showLineNumbers = true,
    lineBreaks = false,
    maxHeight,
    size = 'default',
  } = props;

  const stratoLogger = getStratoLogger('CodeSnippet');

  const {
    highlightCode,
    noHighlightInline,
    noHighlightCode,
    noHighlightLineNumbers,
  } = useMemo(() => splitCodeForHighlighting(code), [code]);

  const isNonHighlightedMultiLine = noHighlightLineNumbers.includes('\n');
  const hasNonHighlightedBlock = noHighlightCode.length > 0;
  const isFullyHighlighted =
    noHighlightInline.length === 0 && !hasNonHighlightedBlock;

  return (
    <pre
      className={styles.highlightWrapper({ size })}
      ref={forwardedRef}
      style={{
        whiteSpace: lineBreaks ? 'pre-wrap' : 'pre',
        maxHeight: maxHeight,
      }}
    >
      <ErrorBoundary
        fallback={code}
        onError={(error, info) => {
          stratoLogger.error(error.message, error, {
            code,
            info,
          });
        }}
      >
        <HighlightExtended language={language} code={highlightCode}>
          {({ tokens, getLineProps, getTokenProps }) => {
            const isSingleLine = tokens.length === 1 && isFullyHighlighted;
            return (
              <>
                {tokens.map((line, i) => {
                  const isLastLine = i === tokens.length - 1;
                  const { className: lineClassName, ...remainingLineProps } =
                    getLineProps({ line });
                  return (
                    <div
                      key={i}
                      className={clsx(lineClassName, styles.line)}
                      {...remainingLineProps}
                    >
                      {showLineNumbers ? (
                        <span
                          className={clsx(
                            'line-number',
                            styles.lineNumberContainer(),
                          )}
                        >
                          <span
                            className={styles.lineNumber({ size })}
                            data-linenumber={i + 1}
                          />
                        </span>
                      ) : null}
                      <span
                        className={styles.lineContent({ size })}
                        style={{
                          verticalAlign: isSingleLine ? 'middle' : undefined,
                        }}
                      >
                        {line.map((token, index) => (
                          <span key={index} {...getTokenProps({ token })} />
                        ))}
                        {/* Append the non-highlighted inline continuation so it
                            stays on the same visual line (same table-row) as the
                            last highlighted token — no extra line number. */}
                        {isLastLine && noHighlightInline.length > 0
                          ? noHighlightInline
                          : null}
                      </span>
                    </div>
                  );
                })}
                {hasNonHighlightedBlock ? (
                  <NonHighlightedBlock
                    showLineNumbers={showLineNumbers}
                    size={size}
                    isMultiLine={isNonHighlightedMultiLine}
                    lineNumbers={noHighlightLineNumbers}
                    code={noHighlightCode}
                  />
                ) : null}
              </>
            );
          }}
        </HighlightExtended>
      </ErrorBoundary>
    </pre>
  );
});

(CodeHighlight as typeof CodeHighlight & { displayName: string }).displayName =
  'CodeHighlight';
